import { useCallback, useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import { CHAPTER_COUNT } from "../data/experience";
import { normalizedChapterProgress } from "../lib/experience-machine";
import {
  resetScrollMotion,
  updateScrollMotion,
} from "../lib/scroll-motion";
import { useExperienceStore } from "../store/experience-store";

gsap.registerPlugin(ScrollTrigger);

export function useScrollTimeline(): Readonly<{
  scrollToChapter: (index: number, immediate?: boolean, duration?: number) => void;
  scrollToEnd: (duration: number, onComplete: () => void) => void;
  cancelProgrammaticScroll: () => void;
}> {
  const lenisRef = useRef<Lenis | null>(null);
  const setTimeline = useExperienceStore((store) => store.setTimeline);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      smoothWheel: !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
      syncTouch: false,
    });
    lenisRef.current = lenis;

    const updateLenis = (time: number): void => lenis.raf(time * 1000);
    const updateScrollTrigger = (scroll: Lenis): void => {
      updateScrollMotion(scroll.velocity, scroll.direction);
      ScrollTrigger.update();
    };
    gsap.ticker.add(updateLenis);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", updateScrollTrigger);

    const trigger = ScrollTrigger.create({
      trigger: "#story",
      start: "top top",
      end: "bottom bottom",
      onUpdate: ({ progress }) => {
        const chapter = normalizedChapterProgress(progress, CHAPTER_COUNT);
        setTimeline(progress, chapter.chapterIndex, chapter.chapterProgress);
      },
    });

    ScrollTrigger.refresh();

    return () => {
      trigger.kill();
      lenis.off("scroll", updateScrollTrigger);
      gsap.ticker.remove(updateLenis);
      lenis.destroy();
      lenisRef.current = null;
      resetScrollMotion();
    };
  }, [setTimeline]);

  const scrollToChapter = useCallback(
    (index: number, immediate = false, duration = 1.2): void => {
      const targetIndex = Math.min(CHAPTER_COUNT - 1, Math.max(0, index));
      const section = document.querySelector<HTMLElement>(
        `[data-chapter="${targetIndex}"]`,
      );
      if (!section) return;
      const chapterOffset =
        targetIndex === 1
          ? window.innerHeight * 0.34
          : targetIndex === 2
            ? window.innerHeight * 0.18
            : 0;
      lenisRef.current?.scrollTo(section, {
        offset: chapterOffset,
        duration: immediate ? 0 : duration,
        immediate: immediate || window.matchMedia("(prefers-reduced-motion: reduce)").matches,
        lock: false,
      });
    },
    [],
  );

  const scrollToEnd = useCallback(
    (duration: number, onComplete: () => void): void => {
      const maxScroll = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight,
      );
      lenisRef.current?.scrollTo(maxScroll, {
        duration,
        easing: (progress) => progress,
        lock: false,
        onComplete,
      });
    },
    [],
  );

  const cancelProgrammaticScroll = useCallback((): void => {
    const lenis = lenisRef.current;
    if (!lenis) return;
    lenis.scrollTo(lenis.scroll, { immediate: true, force: true });
  }, []);

  return { scrollToChapter, scrollToEnd, cancelProgrammaticScroll };
}
